interface OfferSectionProps {
  onOpenForm: () => void;
}

const whatYouGet = [
  {
    icon: 'ri-layout-masonry-line',
    title: 'Brand New Modern Website',
    detail: 'Up to 10 pages',
    desc: 'Custom-designed, fast-loading, built to make your business look like the obvious choice.',
  },
  {
    icon: 'ri-search-2-line',
    title: 'SEO Setup + Monthly Optimization',
    detail: 'Ongoing every month',
    desc: 'On-page SEO, keyword strategy, and monthly improvements that compound over time.',
  },
  {
    icon: 'ri-robot-2-line',
    title: 'AI Search Optimization',
    detail: 'ChatGPT, Gemini & more',
    desc: 'Structured so AI tools reference and recommend your business in their answers.',
  },
  {
    icon: 'ri-mic-line',
    title: 'Voice Search Optimization',
    detail: 'Siri, Alexa, Google Assistant',
    desc: 'Natural language content that matches how people speak, not just how they type.',
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Google Maps & Local SEO',
    detail: '"Near me" searches',
    desc: 'Dominate local map results — capture high-intent buyers searching in your area.',
  },
  {
    icon: 'ri-focus-3-line',
    title: 'Conversion-Focused Design',
    detail: 'Calls, forms, and actions',
    desc: 'Every page element engineered to turn visitors into phone calls and paying customers.',
  },
];

export default function OfferSection({ onOpenForm }: OfferSectionProps) {
  return (
    <section id="offer" className="bg-white py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-emerald-500 text-xs font-bold tracking-widest uppercase mb-4">What You Get</p>
          <h2 className="text-3xl md:text-5xl font-black text-[#0d1117] leading-tight mb-4">
            Everything Included.{' '}
            <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
              One Flat Price.
            </span>
          </h2>
          <p className="text-[#6b7280] text-base md:text-lg max-w-xl mx-auto">
            Not just a website. Not just SEO. A complete customer-acquisition system built for 2026 search behavior.
          </p>
        </div>

        {/* 6-Block Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {whatYouGet.map((item) => (
            <div
              key={item.title}
              className="group bg-[#f8faf9] border border-[#e9ecef] rounded-2xl p-6 hover:border-emerald-200 hover:bg-white hover:shadow-md transition-all duration-300"
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 flex-shrink-0 group-hover:from-emerald-100 group-hover:border-emerald-200 transition-all duration-300">
                  <i className={`${item.icon} text-emerald-600`} style={{ fontSize: '20px' }}></i>
                </div>
                <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1 whitespace-nowrap">{item.detail}</span>
              </div>
              <h3 className="text-[#0d1117] font-bold text-base mb-2 leading-snug">{item.title}</h3>
              <p className="text-[#6b7280] text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Pricing block */}
        <div className="bg-[#0d1117] rounded-2xl p-7 md:p-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <p className="text-white/50 text-sm mb-2 uppercase tracking-widest font-semibold">All of the above for just</p>
            <div className="flex items-end gap-3 mb-2">
              <span className="text-5xl md:text-6xl font-black bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent leading-none">$399</span>
              <span className="text-white/50 text-lg pb-1.5">/month</span>
            </div>
            <div className="flex flex-wrap gap-3 mt-3">
              {['$0 upfront website build', '90-day start, then month-to-month', 'No hidden fees'].map((t) => (
                <div key={t} className="flex items-center gap-1.5">
                  <i className="ri-check-line text-emerald-400" style={{ fontSize: '13px' }}></i>
                  <span className="text-white/55 text-sm">{t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button
              onClick={onOpenForm}
              className="whitespace-nowrap bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm px-8 py-4 rounded-full hover:opacity-90 hover:scale-[1.02] transition-all duration-200 cursor-pointer flex items-center gap-2.5"
            >
              Get Started Today
              <i className="ri-arrow-right-line" style={{ fontSize: '16px' }}></i>
            </button>
            <a
              href="tel:8004559670"
              className="whitespace-nowrap flex items-center justify-center gap-2 bg-white/8 border border-white/15 text-white font-semibold text-sm px-6 py-4 rounded-full hover:bg-white/14 transition-all duration-200 cursor-pointer"
            >
              <i className="ri-phone-fill text-emerald-400" style={{ fontSize: '15px' }}></i>
              800-455-9670
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
